import Cookie from 'js-cookie';
import {CookieAttributes} from 'node_modules/@types/js-cookie';

export const COOKIE_YM_UID = '_ym_uid';
export const COOKIE_TMR_LVID = 'tmr_lvid';
export const COOKIE_OID = 'oid';
export const COOKIE_MT_BUNDLE_ID = 'bundle_id';
export const COOKIE_MT_REFERRER = 'mt_referrer';
export const COOKIE_MT_REFERRER_EXPIRE_TIME = 'mt_referrer_expire_time';
export const COOKIE_RSM_QUERY = 'rsm.query';
export const COOKIE_WEB_AUTO_DOWNLOAD = 'web_auto_download';
export const COOKIE_ECOMMERCE_LIST_DATA = 'ecom_app_click_data';
/** для витрины, если кука стоит при запросе страницы /featuring/id
 * и значение куки совпадает с айди подборки - рендерится кнопка назад */
export const COOKIE_FEATURING_WITH_BACK_BUTTON = 'featuring_with_back';

/**
 * После скачивания приложения (.apk) очищаем куки:
 * RSM_QUERY - параметры рекламная кампания, по которой пользователь пришел на сайт
 * MT_BUNDLE_ID - название мобильного приложения для отложенного deepLink
 * MT_REFERRER - источник перехода на сайт
 */
export const resetApkCookies = () => {
  [COOKIE_RSM_QUERY, COOKIE_MT_BUNDLE_ID, COOKIE_MT_REFERRER].forEach(
    (cookie) => deleteCookie(cookie),
  );
};

const api = Cookie.withAttributes({
  path: '/',
  sameSite: 'None',
  secure: true,
});

export const getCookie = (name: string) => {
  try {
    return api.get(name) ?? null;
  } catch (e) {
    console.warn(`getCookie{${name}}: cannot access cookies`, e);
    return null;
  }
};

export const deleteCookie = (name: string) => {
  try {
    api.remove(name);
  } catch (e) {
    console.warn(`deleteCookie(${name}): cannot access cookies`, e);
  }
};

export const setCookie = (
  name: string,
  value: string,
  domainOrAttrs: string | CookieAttributes,
) => {
  try {
    api.set(
      name,
      value,
      typeof domainOrAttrs === 'string'
        ? {domain: domainOrAttrs}
        : domainOrAttrs,
    );
  } catch (e) {
    console.warn(`setCookie(${name}): cannot access cookies`, e);
  }
};

const defaultPollingPeriodMs = 25;
const defaultMaxWaitMs = 2500;

/**
 * @returns promise that resolves either when all cookies are present, or maxWaitMs elapsed
 */
export const waitForCookies = async ({
  names,
  pollingPeriodMs = defaultPollingPeriodMs,
  maxWaitMs = defaultMaxWaitMs,
}: {
  /** names of cookies to check */
  names: Array<string>;
  /** interval between checks */
  pollingPeriodMs?: number;
  /** Promise resolves after this delay even if not all cookies are present */
  maxWaitMs?: number;
}): Promise<Array<string>> =>
  new Promise((resolve) => {
    const getPresentCookies = () => {
      const allCookies = api.get();

      return names.filter((x) => !!allCookies[x]);
    };

    try {
      if (getPresentCookies().length === names.length) {
        resolve(names);

        return;
      }
    } catch (e) {
      console.warn('waitForCookies: cannot access cookies', e);
      resolve([]);
      return;
    }

    const pollingStartTime = Date.now();

    const intervalHandle = setInterval(() => {
      const presentCookies = getPresentCookies();
      const elapsed = Date.now() - pollingStartTime;

      if (presentCookies.length === names.length || elapsed >= maxWaitMs) {
        clearInterval(intervalHandle);
        resolve(presentCookies);
      }
    }, pollingPeriodMs);
  });

const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

/** Специфично для веб-витрины! Только там ставится такая куконька */
export const getUserIpFromCookieInWebCatalog = (): string | null => {
  const value = getCookie('_rcache');

  if (!value) {
    return null;
  }

  try {
    const payload: {ip: string} = parseJwt(value);

    return payload.ip;
  } catch (e) {
    console.error('Failed to parse', e);
  }

  return null;
};

export type EcommerceCookiePayload = {
  packageName: string;
  listName: string;
  listPosition: number;
};

const ECOMMERCE_DATA_SEPARATOR = '|';

export const setEcommerceCookieData = (
  {packageName, listName, listPosition}: EcommerceCookiePayload,
  cookieDomain: string,
) => {
  const value = [packageName, listName, listPosition].join(
    ECOMMERCE_DATA_SEPARATOR,
  );

  setCookie(COOKIE_ECOMMERCE_LIST_DATA, value, cookieDomain);
};

export const getEcommerceCookieData = (): EcommerceCookiePayload | null => {
  const value = getCookie(COOKIE_ECOMMERCE_LIST_DATA);

  if (!value) {
    return null;
  }

  const [packageName, listName, listPosition] = value.split(
    ECOMMERCE_DATA_SEPARATOR,
  );

  return {
    packageName,
    listName,
    listPosition: Number.parseInt(listPosition),
  };
};

export const resetEcommerceData = () => {
  deleteCookie(COOKIE_ECOMMERCE_LIST_DATA);
};
